import axios from 'axios';
import { safeSessionStorage } from './safeStorage';

axios.defaults.withCredentials = true;

// 프런트와 같은 origin의 프록시를 통해 API를 호출한다.
// 개발 서버의 setupProxy와 운영 서버 모두 /api를 백엔드로 전달하므로,
// 모바일에서 LAN IP의 :5000을 직접 호출할 때 발생하는 CORS·쿠키 문제를 피한다.
export const getApiBaseURL = () => {
  return '';
};

const getBaseURL = getApiBaseURL;

// 기본 API 구성
const API_CONFIG = {
  base: getBaseURL(),
  auth: '/api/auth',
  api: '/api'
};

let csrfToken = null;
let csrfRequest = null;

export const ensureCsrfToken = async (force = false) => {
  if (force) csrfToken = null;
  if (csrfToken) return csrfToken;
  if (!csrfRequest) {
    csrfRequest = axios.get(`${getBaseURL()}${API_CONFIG.auth}/csrf`, { withCredentials: true })
      .then((response) => {
        csrfToken = response.data.csrf_token;
        return csrfToken;
      })
      .finally(() => { csrfRequest = null; });
  }
  return csrfRequest;
};

export const getCsrfHeaders = async () => ({ 'X-CSRF-Token': await ensureCsrfToken() });

// 요청 헬퍼 함수
const request = async (method, url, data = null, withAuth = false) => {
  try {
    const config = { method, url };
    
    if (withAuth) {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('인증 토큰이 없습니다.');
      config.headers = { Authorization: `Bearer ${token}` };
    }
    
    if (data) config.data = data;
    
    const response = await axios(config);
    return { success: true, data: response.data };
  } catch (err) {
    return { 
      success: false, 
      error: err.response?.data?.error || '요청 처리 중 오류가 발생했습니다.',
      ...err.response?.data
    };
  }
};

// FormData를 위한 요청 헬퍼 함수
const formDataRequest = async (url, formData, withAuth = false) => {
  try {
    const config = { 
      method: 'post', 
      url,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    };
    
    if (withAuth) {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('인증 토큰이 없습니다.');
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    const response = await axios(config);
    return { success: true, data: response.data };
  } catch (err) {
    return { 
      success: false, 
      error: err.response?.data?.error || '요청 처리 중 오류가 발생했습니다.',
      ...err.response?.data
    };
  }
};

// 법률 검색 API
export const lawAPI = {
  searchLaws: async (keyword, page = 1) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/laws/search?keyword=${encodeURIComponent(keyword)}&page=${page}`;
    return request('get', url);
  },

  getLawsByDepartment: async (orgCode) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/laws/department/${orgCode}`;
    return request('get', url);
  },

  getLawsByDate: async (startDate, endDate) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/laws/date?start_date=${startDate}&end_date=${endDate}`;
    return request('get', url);
  }
};

// 댓글 관련 API
export const commentAPI = {
  getComments: async (qaId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/comments/${qaId}`;
    return request('get', url, null, true);
  },

  createComment: async (qaId, content) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/comments/${qaId}`;
    return request('post', url, { content }, true);
  },

  updateComment: async (commentId, content) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/comments/${commentId}`;
    return request('put', url, { content }, true);
  },

  deleteComment: async (commentId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/comments/${commentId}`;
    return request('delete', url, null, true);
  }
};

// 사용자 인증 관련 API - AuthContext에서 사용할 수 있음
export const authAPI = {
  me: async () => {
    const response = await axios.get(`${API_CONFIG.base}${API_CONFIG.auth}/me`, { withCredentials: true });
    return response.data;
  },

  login: async (identity, password, remember = false) => {
    const url = `${API_CONFIG.base}${API_CONFIG.auth}/login`;
    const response = await axios.post(url, { identity, password, remember }, { headers: await getCsrfHeaders() });
    csrfToken = response.data.csrf_token || csrfToken;
    return response.data;
  },
  
  logout: async () => {
    const url = `${API_CONFIG.base}${API_CONFIG.auth}/logout`;
    const response = await axios.post(url, {}, { headers: await getCsrfHeaders() });
    csrfToken = null;
    return response.data;
  },
  
  register: async (userData) => {
    const url = `${API_CONFIG.base}${API_CONFIG.auth}/register`;
    const response = await axios.post(url, userData, { headers: await getCsrfHeaders() });
    return response.data;
  },
  
  verifyEmail: async (token) => {
    const response = await axios.post(`${API_CONFIG.base}${API_CONFIG.auth}/verify-email`, { token }, { headers: await getCsrfHeaders() });
    return response.data;
  },
  
  requestPasswordReset: async (email) => {
    const response = await axios.post(`${API_CONFIG.base}${API_CONFIG.auth}/password-reset/request`, { email }, { headers: await getCsrfHeaders() });
    return response.data;
  },
  
  resetPassword: async (token, password) => {
    const response = await axios.post(`${API_CONFIG.base}${API_CONFIG.auth}/password-reset/confirm`, { token, password }, { headers: await getCsrfHeaders() });
    return response.data;
  },

  oauthUrl: (provider) => `${API_CONFIG.base}${API_CONFIG.auth}/oauth/${provider}`,
  updateAccount: async (username) => {
    const response = await axios.patch(`${API_CONFIG.base}${API_CONFIG.auth}/account`, { username }, { headers: await getCsrfHeaders() });
    return response.data;
  },
  deleteAccount: async (confirmation) => {
    const response = await axios.delete(`${API_CONFIG.base}${API_CONFIG.auth}/account`, { data: { confirmation }, headers: await getCsrfHeaders() });
    csrfToken = null;
    return response.data;
  },
};

// Q&A 관련 API
export const qnaAPI = {
  // 질문 목록 가져오기
  getQuestions: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    // 파라미터가 있으면 추가
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.category && params.category !== 'all') queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);
    
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions?${queryParams.toString()}`;
    return request('get', url);
  },
  
  // 질문 상세 정보 가져오기
  getQuestionDetail: async (questionId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions/${questionId}`;
    return request('get', url);
  },
  
  // 새 질문 작성하기
  createQuestion: async (formData) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions`;
    return formDataRequest(url, formData, true);
  },
  
  // 질문에 답변 작성하기
  createAnswer: async (questionId, answerData) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions/${questionId}/answers`;
    return request('post', url, answerData, true);
  },
  
  // 답변 채택하기
  acceptAnswer: async (questionId, answerId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions/${questionId}/answers/${answerId}/accept`;
    return request('put', url, null, true);
  },
  
  // 질문에 좋아요 추가하기
  likeQuestion: async (questionId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions/${questionId}/like`;
    return request('post', url, null, true);
  },
  
  // 답변에 좋아요 추가하기
  likeAnswer: async (questionId, answerId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions/${questionId}/answers/${answerId}/like`;
    return request('post', url, null, true);
  },
  
  // 질문 삭제하기
  deleteQuestion: async (questionId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions/${questionId}`;
    return request('delete', url, null, true);
  },
  
  // 답변 삭제하기
  deleteAnswer: async (questionId, answerId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/questions/${questionId}/answers/${answerId}`;
    return request('delete', url, null, true);
  }
};

// 안전 포럼 관련 API
export const forumAPI = {
  // 게시물 목록 가져오기
  getPosts: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    // 파라미터가 있으면 추가
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.category) queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);
    
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts?${queryParams.toString()}`;
    return request('get', url);
  },
  
  // 게시물 상세 정보 가져오기
  getPostDetail: async (postId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts/${postId}`;
    return request('get', url);
  },
  
  // 새 게시물 작성하기
  createPost: async (formData) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts`;
    return formDataRequest(url, formData, true);
  },
  
  // 댓글 작성하기
  createComment: async (postId, commentData) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts/${postId}/comments`;
    return request('post', url, commentData, true);
  },
  
  // 게시물 좋아요 추가하기
  likePost: async (postId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts/${postId}/like`;
    return request('post', url, null, true);
  },
  
  // 댓글 좋아요 추가하기
  likeComment: async (postId, commentId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts/${postId}/comments/${commentId}/like`;
    return request('post', url, null, true);
  },
  
  // 게시물 공유하기
  sharePost: async (postId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts/${postId}/share`;
    return request('post', url, null, true);
  },
  
  // 최신 인기 게시물 가져오기
  getTrendingPosts: async (limit = 5) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts/trending?limit=${limit}`;
    return request('get', url);
  },
  
  // 사용자별 게시물 가져오기
  getUserPosts: async (userId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${API_CONFIG.base}${API_CONFIG.api}/users/${userId}/posts?${queryParams}`;
    return request('get', url, null, true);
  },
  
  // 게시물 삭제하기
  deletePost: async (postId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts/${postId}`;
    return request('delete', url, null, true);
  },
  
  // 댓글 삭제하기
  deleteComment: async (postId, commentId) => {
    const url = `${API_CONFIG.base}${API_CONFIG.api}/posts/${postId}/comments/${commentId}`;
    return request('delete', url, null, true);
  }
};

// 자유게시판 API
export const boardAPI = {
  // 관리자 토큰(세션 기반)이 있으면 요청에 Authorization 헤더를 붙입니다.
  // (admin 페이지 로그인 후 다른 메뉴로 이동해도 sessionStorage가 유지됨)
  _getAdminHeaders: () => {
    const token = safeSessionStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
  getList: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.page) q.append('page', params.page);
    if (params.per_page) q.append('per_page', params.per_page);
    if (params.category) q.append('category', params.category);
    if (params.search) q.append('search', params.search);
    if (params.search_type) q.append('search_type', params.search_type);
    if (params.sort) q.append('sort', params.sort);
    const url = `${getBaseURL()}/api/board/posts?${q.toString()}`;
    const res = await axios.get(url);
    return res.data;
  },
  getDetail: async (postId) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}`;
    const res = await axios.get(url);
    return res.data;
  },
  create: async (data, file) => {
    const url = `${getBaseURL()}/api/board/posts`;
    if (file) {
      const fd = new FormData();
      ['title', 'content', 'author', 'category', 'edit_password', 'source_name', 'source_url'].forEach((k) => {
        if (data[k] != null && data[k] !== undefined) {
          fd.append(k, String(data[k]));
        }
      });
      fd.append('legal_references', JSON.stringify(data.legal_references || []));
      if (data.parent_id != null && data.parent_id !== undefined) {
        fd.append('parent_id', String(data.parent_id));
      }
      fd.append('file', file);
      const res = await axios.post(url, fd, { headers: { ...boardAPI._getAdminHeaders(), ...(await getCsrfHeaders()) } });
      return res.data;
    }
    const res = await axios.post(url, data, { headers: { 'Content-Type': 'application/json', ...boardAPI._getAdminHeaders(), ...(await getCsrfHeaders()) } });
    return res.data;
  },
  /**
   * @param {object} [options] - { file?: File, removeAttachment?: boolean }
   */
  update: async (postId, data, options = {}) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}`;
    const { file, removeAttachment } = options;
    if (file || removeAttachment) {
      const fd = new FormData();
      fd.append('title', data.title);
      fd.append('content', data.content);
      fd.append('category', data.category);
      fd.append('source_name', data.source_name || '');
      fd.append('source_url', data.source_url || '');
      fd.append('legal_references', JSON.stringify(data.legal_references || []));
      if (data.edit_password != null && data.edit_password !== undefined && data.edit_password !== '') {
        fd.append('edit_password', data.edit_password);
      }
      if (removeAttachment) {
        fd.append('remove_attachment', '1');
      }
      if (file) {
        fd.append('file', file);
      }
      const res = await axios.put(url, fd, { headers: { ...boardAPI._getAdminHeaders(), ...(await getCsrfHeaders()) } });
      return res.data;
    }
    const res = await axios.put(url, data, { headers: { 'Content-Type': 'application/json', ...boardAPI._getAdminHeaders(), ...(await getCsrfHeaders()) } });
    return res.data;
  },
  deletePost: async (postId, data) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}`;
    const res = await axios.delete(url, { data, headers: { 'Content-Type': 'application/json', ...boardAPI._getAdminHeaders(), ...(await getCsrfHeaders()) } });
    return res.data;
  },
  getCategories: async () => {
    const url = `${getBaseURL()}/api/board/categories`;
    const res = await axios.get(url);
    return res.data;
  },
  searchLegalReferences: async (query) => {
    const url = `${getBaseURL()}/api/board/legal-reference-suggestions?q=${encodeURIComponent(query)}`;
    const res = await axios.get(url);
    return res.data;
  },
  uploadImage: async (file) => {
    const form = new FormData();
    form.append('image', file);
    const res = await axios.post(`${getBaseURL()}/api/upload/image`, form, {
      headers: await getCsrfHeaders(),
    });
    return res.data;
  },
  getComments: async (postId) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}/comments`;
    const res = await axios.get(url);
    return res.data;
  },
  createComment: async (postId, data) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}/comments`;
    const res = await axios.post(url, data, { headers: { 'Content-Type': 'application/json', ...(await getCsrfHeaders()) } });
    return res.data;
  },
  updateComment: async (postId, commentId, data) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}/comments/${commentId}`;
    const res = await axios.put(url, data, { headers: { 'Content-Type': 'application/json', ...boardAPI._getAdminHeaders(), ...(await getCsrfHeaders()) } });
    return res.data;
  },
  deleteComment: async (postId, commentId) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}/comments/${commentId}`;
    const res = await axios.delete(url, { headers: { ...boardAPI._getAdminHeaders(), ...(await getCsrfHeaders()) } });
    return res.data;
  },
  toggleLike: async (postId) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}/like`;
    const res = await axios.post(url, {}, { headers: await getCsrfHeaders() });
    return res.data;
  },
  claimPost: async (postId, editPassword) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}/claim`;
    const res = await axios.post(url, { edit_password: editPassword }, { headers: await getCsrfHeaders() });
    return res.data;
  },
  report: async (postId, data) => {
    const url = `${getBaseURL()}/api/board/posts/${postId}/reports`;
    const res = await axios.post(url, data, { headers: await getCsrfHeaders() });
    return res.data;
  },
};

const getFieldQnaClientId = () => {
  const key = 'field_qna_client_id';
  let value = localStorage.getItem(key);
  if (!value) {
    value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, value);
  }
  return value;
};

const fieldQnaOwnerKey = (type, id) => `field_qna_owner_${type}_${id}`;
const getFieldQnaOwner = (type, id) => localStorage.getItem(fieldQnaOwnerKey(type, id)) || '';
const saveFieldQnaOwner = (type, id, token) => token && localStorage.setItem(fieldQnaOwnerKey(type, id), token);
const fieldQnaHeaders = (ownerToken = '') => {
  const adminToken = safeSessionStorage.getItem('admin_token');
  return {
    'X-Field-Qna-Client': getFieldQnaClientId(),
    ...(ownerToken ? { 'X-Field-Qna-Owner': ownerToken } : {}),
    ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
  };
};

export const fieldQnaAPI = {
  searchEvidence: async (query) => {
    const res = await axios.get(`${getBaseURL()}/api/field-qna/evidence-suggestions?q=${encodeURIComponent(query)}`, {
      headers: fieldQnaHeaders(),
    });
    return res.data;
  },
  trackEvent: async (eventType, data = {}) => {
    const res = await axios.post(`${getBaseURL()}/api/field-qna/events`, {
      event_type: eventType,
      ...data,
    }, { headers: fieldQnaHeaders() });
    return res.data;
  },
  uploadImage: async (file) => {
    const form = new FormData();
    form.append('image', file);
    const res = await axios.post(`${getBaseURL()}/api/field-qna/images`, form, { headers: fieldQnaHeaders() });
    return res.data.url;
  },
  getList: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== '' && value != null) query.set(key, value);
    });
    const res = await axios.get(`${getBaseURL()}/api/field-qna/questions?${query}`, { headers: fieldQnaHeaders() });
    return res.data;
  },
  getDetail: async (id) => {
    const res = await axios.get(`${getBaseURL()}/api/field-qna/questions/${id}`, { headers: fieldQnaHeaders(getFieldQnaOwner('question', id)) });
    return res.data;
  },
  createQuestion: async (data) => {
    const res = await axios.post(`${getBaseURL()}/api/field-qna/questions`, data, { headers: fieldQnaHeaders() });
    saveFieldQnaOwner('question', res.data.question.id, res.data.owner_token);
    return res.data;
  },
  updateQuestion: async (id, data) => {
    const res = await axios.put(`${getBaseURL()}/api/field-qna/questions/${id}`, data, { headers: fieldQnaHeaders(getFieldQnaOwner('question', id)) });
    return res.data;
  },
  deleteQuestion: async (id, editPassword) => {
    const res = await axios.delete(`${getBaseURL()}/api/field-qna/questions/${id}`, {
      data: { edit_password: editPassword }, headers: fieldQnaHeaders(getFieldQnaOwner('question', id)),
    });
    return res.data;
  },
  createAnswer: async (id, data) => {
    const res = await axios.post(`${getBaseURL()}/api/field-qna/questions/${id}/answers`, data, { headers: fieldQnaHeaders() });
    saveFieldQnaOwner('answer', res.data.answer.id, res.data.owner_token);
    return res.data;
  },
  updateAnswer: async (id, data) => {
    const res = await axios.put(`${getBaseURL()}/api/field-qna/answers/${id}`, data, { headers: fieldQnaHeaders(getFieldQnaOwner('answer', id)) });
    return res.data;
  },
  deleteAnswer: async (id, editPassword) => {
    const res = await axios.delete(`${getBaseURL()}/api/field-qna/answers/${id}`, {
      data: { edit_password: editPassword }, headers: fieldQnaHeaders(getFieldQnaOwner('answer', id)),
    });
    return res.data;
  },
  acceptAnswer: async (questionId, answerId, editPassword) => {
    const res = await axios.post(
      `${getBaseURL()}/api/field-qna/questions/${questionId}/accept/${answerId}`,
      { edit_password: editPassword },
      { headers: fieldQnaHeaders(getFieldQnaOwner('question', questionId)) }
    );
    return res.data;
  },
  likeQuestion: async (id) => {
    const res = await axios.post(`${getBaseURL()}/api/field-qna/questions/${id}/like`, {}, { headers: fieldQnaHeaders() });
    return res.data;
  },
  likeAnswer: async (id) => {
    const res = await axios.post(`${getBaseURL()}/api/field-qna/answers/${id}/like`, {}, { headers: fieldQnaHeaders() });
    return res.data;
  },
  report: async (targetType, targetId, reason) => {
    const res = await axios.post(`${getBaseURL()}/api/field-qna/report`, {
      target_type: targetType, target_id: targetId, reason,
    }, { headers: fieldQnaHeaders() });
    return res.data;
  },
  owns: (type, id) => Boolean(getFieldQnaOwner(type, id)),
  getOwnedIds: (type) => Object.keys(localStorage)
    .filter((key) => key.startsWith(`field_qna_owner_${type}_`))
    .map((key) => Number(key.replace(`field_qna_owner_${type}_`, '')))
    .filter(Number.isFinite),
};

const apiExports = {
  law: lawAPI,
  comment: commentAPI,
  auth: authAPI,
  qna: qnaAPI,
  forum: forumAPI,
  board: boardAPI,
  fieldQna: fieldQnaAPI
};

export default apiExports;
